import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import {
  Box, Stack, Typography, IconButton, Tooltip, Button, Skeleton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import { axios, baseUrl } from 'Api';
import { AppContext, AuthContext } from 'Context';
import { Snackbar } from 'Components';
import { Users } from 'Utils/lucideIcons';
import { AnimatedTrash } from 'Components/reactbits/AnimatedIcons';
import { V2ThemeProvider } from 'Pages/private/v2/_shared/V2Layout';
import PillSearch from 'Components/PillSearch';
import { isNewUIEnabled } from 'hooks/useNewUI';
import { ViewerPage } from '../components/page';
import { ConnectedAccountsClassic } from './ConnectedAccountsClassic';

const defaultSnackbar = { isVisible: false, type: undefined, message: undefined };

// Viewers → Connected Accounts. Same card/table chrome as the new-UI Viewers
// tab (ViewersTable); the data is the affiliate's connected accounts and
// removal goes through removeInviteManager.
const ConnectedAccountsNew = () => {
  const { getToken } = useContext(AuthContext);
  const { isTokenChange, langText } = useContext(AppContext);
  const commonText = langText?.common;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const [search, setSearch] = useState('');
  const [snackbar, setSnackbar] = useState(defaultSnackbar);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.post(`${baseUrl}/inviteUsersList`, { token }).catch(() => null);
      const data = res?.data;
      const list = Array.isArray(data) ? data : (data?.data || data?.users || []);
      setUsers(Array.isArray(list) ? list : []);
    } catch (e) {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { loadUsers(); }, [loadUsers, isTokenChange]);

  // Same call the old user-management list used (UserList.jsx → removeInviteManager).
  const handleRemove = async (u) => {
    if (!u?.id) return;
    setRemovingId(u.id);
    try {
      const token = await getToken();
      const { data } = await axios.post(`${baseUrl}/removeInviteManager`, { token, id: u.id });
      if (data?.success === false) {
        setSnackbar({ isVisible: true, type: 'error', message: data?.message || 'Oops! Something went wrong' });
        return;
      }
      setUsers((prev) => prev.filter((row) => row.id !== u.id));
      setSnackbar({
        isVisible: true,
        type: 'success',
        message: `${u?.firstname ? `${u.firstname}` : 'User'} removed`,
      });
    } catch (error) {
      setSnackbar({
        isVisible: true,
        type: 'error',
        message: error?.response?.data?.message || 'Oops! Something went wrong',
      });
    } finally {
      setRemovingId(null);
    }
  };

  // Invite a new connected account — same endpoint as the classic popup
  // (inviteManager), shown here in a new-style dialog.
  const handleInvite = async () => {
    const email = inviteEmail.trim();
    if (!email) return;
    setInviting(true);
    try {
      const token = await getToken();
      const { data } = await axios.post(`${baseUrl}/inviteManager`, { token, email: [email] });
      if (data?.success) {
        setSnackbar({ isVisible: true, type: 'success', message: data?.message || 'Invitation sent' });
        setInviteOpen(false);
        setInviteEmail('');
        loadUsers();
      } else {
        setSnackbar({ isVisible: true, type: 'error', message: data?.message || 'Could not send invitation' });
      }
    } catch (error) {
      setSnackbar({ isVisible: true, type: 'error', message: error?.response?.data?.message || 'Could not send invitation' });
    } finally {
      setInviting(false);
    }
  };

  const q = search.trim().toLowerCase();
  const shown = q
    ? users.filter((u) => (
      `${u.firstname || ''} ${u.lastname || ''}`.toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
    ))
    : users;

  return (
    <>
      <ViewerPage
        title="Connected Accounts"
        subTitle="Accounts connected to your affiliate program — the users you created or invited."
        hideHeaderActions
      >
        <V2ThemeProvider withBaseline={false}>
          <Box
            sx={{
              bgcolor: '#ffffff',
              border: '1px solid #e6e8ec',
              borderRadius: '16px',
              boxShadow: '0 1px 3px rgba(15,17,28,0.06)',
              p: { xs: 1.5, md: 2 },
              boxSizing: 'border-box',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' }, mb: 1.5 }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#15171c' }}>
                  Connected Accounts
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
                  {`${users.length} connected`}
                </Typography>
              </Box>
              <PillSearch
                value={search}
                onChange={setSearch}
                placeholder="Search accounts"
                minWidth={180}
              />
            </Stack>

            {loading ? (
              <Stack sx={{ py: 0.5 }}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Stack
                    key={i}
                    direction="row"
                    spacing={1.25}
                    sx={{ alignItems: 'center', py: 1.25, px: 1 }}
                  >
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="text" width="26%" sx={{ fontSize: 14 }} />
                    <Skeleton variant="text" width="34%" sx={{ fontSize: 14 }} />
                    <Skeleton variant="circular" width={26} height={26} sx={{ ml: 'auto' }} />
                  </Stack>
                ))}
              </Stack>
            ) : !shown.length ? (
              <Stack spacing={1.25} sx={{ alignItems: 'center', py: 6, color: '#6b7280' }}>
                <Users size={28} />
                <Typography variant="body2">
                  {q ? `No accounts match “${search.trim()}”` : 'No connected accounts yet'}
                </Typography>
                {!q && (
                  <Button
                    variant="contained"
                    onClick={() => setInviteOpen(true)}
                    sx={{
                      mt: 0.5, textTransform: 'none', fontWeight: 700, borderRadius: 999, px: 2.5,
                    }}
                  >
                    Add Connected account
                  </Button>
                )}
              </Stack>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                  <Box component="thead">
                    <Box
                      component="tr"
                      sx={{ '& th': { textAlign: 'left', py: 1, px: 1, fontSize: 12, fontWeight: 700, color: '#9aa1ab', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' } }}
                    >
                      <Box component="th">Name</Box>
                      <Box component="th">Email</Box>
                      <Box component="th" sx={{ textAlign: 'right !important' }}>Action</Box>
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {shown.map((u, idx) => {
                      const name = `${u?.firstname || ''} ${u?.lastname || ''}`.trim()
                        || u?.email?.split('@')[0] || '—';
                      const image = u?.dpURL || u?.image;
                      const busy = removingId === u?.id;
                      return (
                        <Box
                          component="tr"
                          key={u?.id || u?.email || idx}
                          sx={{ '& td': { py: 1.25, px: 1, fontSize: 14, verticalAlign: 'middle' }, '&:hover td': { bgcolor: '#f8f9fb' } }}
                        >
                          <Box component="td">
                            <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                              {image ? (
                                <Box
                                  component="img"
                                  src={image}
                                  alt={name}
                                  sx={{
                                    width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
                                  }}
                                />
                              ) : (
                                <Box sx={{
                                  width: 32, height: 32, borderRadius: '50%', bgcolor: '#eef1f6', color: '#4b5563', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0, textTransform: 'uppercase',
                                }}
                                >
                                  {name.charAt(0)}
                                </Box>
                              )}
                              <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#15171c' }} noWrap>
                                {name}
                              </Typography>
                            </Stack>
                          </Box>
                          <Box component="td">
                            <Typography variant="body2" sx={{ color: '#6b7280' }} noWrap>{u?.email}</Typography>
                          </Box>
                          <Box component="td" sx={{ textAlign: 'right' }}>
                            {busy ? (
                              <Button size="small" disabled sx={{ textTransform: 'none' }}>
                                {`${commonText?.removing || 'Removing'}...`}
                              </Button>
                            ) : (
                              <Tooltip title={commonText?.remove || 'Remove'}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemove(u)}
                                  sx={{ color: '#d3302f' }}
                                >
                                  <AnimatedTrash size={17} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </V2ThemeProvider>
      </ViewerPage>

      {/* New-style invite popup (new UI env). */}
      <Dialog
        open={inviteOpen}
        onClose={() => { if (!inviting) setInviteOpen(false); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#15171c' }}>Add connected account</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#6b7280', mb: 1.5 }}>
            Enter the email of the person you want to connect to your affiliate program.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="email"
            placeholder="you@example.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && inviteEmail.includes('@') && !inviting) handleInvite(); }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setInviteOpen(false)}
            disabled={inviting}
            sx={{ textTransform: 'none', color: '#6b7280' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={inviting || !inviteEmail.includes('@')}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
          >
            {inviting ? 'Inviting…' : 'Send invite'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        isVisible={snackbar?.isVisible}
        message={snackbar?.message || ''}
        onClose={() => setSnackbar(defaultSnackbar)}
        type={snackbar?.type}
      />
    </>
  );
};

export const ConnectedAccounts = (props) => (
  isNewUIEnabled() ? <ConnectedAccountsNew {...props} /> : <ConnectedAccountsClassic {...props} />
);

export default ConnectedAccounts;
